import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePreFilterStore } from '@/store/usePreFilterStore'
import { PrivateSuggestion, useFallbackStore } from '@/store/useFallbackStore'
import { useStepbarStore } from '@/store/useStepbarStore'

import { sendEvent } from '@/services/mixPanelService'

import { useLoading } from './useLoading'
import { useScheduleStore } from '@/store/useScheduleStore'
import { useGenericErrorMixpanel } from './useGenericErrorMixpanel'

const FALLBACK_EVENTS = {
  CUSTOM_FALLBACK: 'customFallback',
  GENERIC_ERROR: 'genericError',
  NO_SLOTS: 'noSlots',
  PATIENT_RESTRICTION: 'patientRestriction',
  COVERAGE_RESTRICTION: 'coverageRestriction',
  VALIDATION_ERROR: 'validationError'
} as const

const FALLBACK_REASON = {
  SPECIALTY_NO_CARE_AT_SELECTED_LOCATION:
    'especialidade-sem-atendimento-no-local-escolhido',
  PLAN_NO_CARE_SPECIALTY_AT_LOCAL:
    'plano-sem-atendimento-para-especialidade-no-local',
  SPECIALTY_NO_CARE_ONLINE: 'especialidade-sem-atendimento-online'
}

interface SendEventFallbackProps {
  label: string
  type: string
  message?: string
}

export const useFallback = () => {
  const navigate = useNavigate()
  const { close: stopLoading } = useLoading()
  const setStepWithError = useStepbarStore((state) => state.setStepWithError)
  const setSuggestion = useFallbackStore((state) => state.setSuggestion)
  const setPrivateSuggestion = useFallbackStore(
    (state) => state.setPrivateSuggestion
  )
  const setShouldShowPreFilter = usePreFilterStore(
    (state) => state.setShouldShowPreFilter
  )
  const isDigitalConsultation = useScheduleStore(
    (s) => s.product?.isDigitalConsultation
  )
  const { doctor } = useScheduleStore.getState()
  const isDoctorFlow = Boolean(doctor)
  const { sendMixpanelEvent } = useGenericErrorMixpanel()

  const _buildPrivateDoctorSuggestion = useCallback(
    (data: PrivateSuggestion): PrivateSuggestion => {
      return {
        ...data,
        ...(doctor ? { name: doctor.name, flow: 'doctor' } : {})
      }
    },
    [doctor]
  )

  const handleStepWithError = useCallback(
    ({ data, name }) => {
      if (
        name === FALLBACK_EVENTS.NO_SLOTS ||
        name === FALLBACK_EVENTS.GENERIC_ERROR
      ) {
        setStepWithError('/agendar-consulta')
        return
      }

      const hasFallbackReason = Boolean(data?.fallbackReason?.length)
      const fallbackReason = hasFallbackReason && data.fallbackReason[0]

      if (!hasFallbackReason) {
        return
      }

      if (
        fallbackReason ===
          FALLBACK_REASON.SPECIALTY_NO_CARE_AT_SELECTED_LOCATION ||
        fallbackReason === FALLBACK_REASON.SPECIALTY_NO_CARE_ONLINE
      ) {
        setStepWithError('/local')
      }

      if (fallbackReason === FALLBACK_REASON.PLAN_NO_CARE_SPECIALTY_AT_LOCAL) {
        setStepWithError('/pagamento')
      }
    },
    [setStepWithError]
  )

  const handleNoSlotsError = useCallback(
    (eventData) => {
      const { data } = eventData
      if (
        data?.fallbackSuggestion?.length ||
        Object.entries(data?.privateSuggestion || {}).length
      ) {
        navigate('/fallback/sugestao-sessenta-dias', { replace: true })
        return
      }

      navigate('/fallback/sessenta-dias', { replace: true })
    },
    [navigate]
  )

  const handlePatientRestrictionError = useCallback(
    ({ data }) => {
      setStepWithError('/paciente')
      navigate(`/fallback/${data.fallbackReason[0]}`, { replace: true })
    },
    [navigate, setStepWithError]
  )

  const handleCustomFallbackError = useCallback(
    (data) => {
      if (data.type === 'sulamerica-individual-pe') {
        setStepWithError('/pagamento')
        navigate(`/fallback/${data.type}`, { replace: true })
      }
    },
    [navigate, setStepWithError]
  )

  const handleCoverageRestrictionError = useCallback(
    ({ data }) => {
      const hasFallbackSuggestion = Boolean(data?.fallbackSuggestion?.length)
      const hasPrivateSuggestion =
        Object.values(data?.privateSuggestion).length > 0
      const fallbackReason = data.fallbackReason[0]

      const hasPrivateSuggestionAndNotIsDigitalConsultation =
        hasPrivateSuggestion && !isDigitalConsultation

      if (
        (hasFallbackSuggestion ||
          hasPrivateSuggestionAndNotIsDigitalConsultation) &&
        data.fallbackReason.includes(
          FALLBACK_REASON.SPECIALTY_NO_CARE_AT_SELECTED_LOCATION
        )
      ) {
        setStepWithError('/local')
        navigate('/fallback/sugestao-expansao-atendimento', { replace: true })
        return
      }

      if (
        (hasFallbackSuggestion ||
          hasPrivateSuggestionAndNotIsDigitalConsultation) &&
        fallbackReason === FALLBACK_REASON.PLAN_NO_CARE_SPECIALTY_AT_LOCAL
      ) {
        setStepWithError('/pagamento')
        navigate('/fallback/sugestao-cobertura', { replace: true })
        return
      }

      navigate(`/fallback/${fallbackReason}`, { replace: true })
    },
    [isDigitalConsultation, navigate, setStepWithError]
  )

  const handleSuggestion = useCallback(
    (eventData) => {
      if (!eventData?.data) return

      const { data } = eventData

      const hasFallbackSuggestion = Boolean(data?.fallbackSuggestion?.length)
      const hasPrivateSuggestion =
        data?.privateSuggestion &&
        Object.values(data?.privateSuggestion).length > 0

      if (hasFallbackSuggestion) {
        setSuggestion(data?.fallbackSuggestion)
      }

      if (hasPrivateSuggestion) {
        const privateSuggestion = !isDoctorFlow
          ? data.privateSuggestion
          : _buildPrivateDoctorSuggestion(data.privateSuggestion)

        setPrivateSuggestion(privateSuggestion)
      }
    },
    [
      _buildPrivateDoctorSuggestion,
      isDoctorFlow,
      setPrivateSuggestion,
      setSuggestion
    ]
  )

  const handleFallbackEvent = useCallback(
    (eventData) => {
      setShouldShowPreFilter(false)
      stopLoading()
      handleStepWithError(eventData)
      handleSuggestion(eventData)

      if (
        eventData.name === FALLBACK_EVENTS.GENERIC_ERROR ||
        eventData.name === FALLBACK_EVENTS.VALIDATION_ERROR
      ) {
        sendMixpanelEvent('handleFallbackEvent')
        return navigate('/fallback/erro-generico', { replace: true })
      }

      if (eventData.name === FALLBACK_EVENTS.NO_SLOTS) {
        return handleNoSlotsError(eventData)
      }

      if (eventData.name === FALLBACK_EVENTS.PATIENT_RESTRICTION) {
        return handlePatientRestrictionError(eventData)
      }

      if (eventData.name === FALLBACK_EVENTS.CUSTOM_FALLBACK) {
        handleCustomFallbackError(eventData.data)

        return
      }

      if (eventData.name === FALLBACK_EVENTS.COVERAGE_RESTRICTION) {
        return handleCoverageRestrictionError(eventData)
      }
    },
    [
      setShouldShowPreFilter,
      stopLoading,
      handleStepWithError,
      handleSuggestion,
      sendMixpanelEvent,
      navigate,
      handleNoSlotsError,
      handlePatientRestrictionError,
      handleCustomFallbackError,
      handleCoverageRestrictionError
    ]
  )

  const sendEventFallback = useCallback(
    ({ label, type, message }: SendEventFallbackProps) => {
      sendEvent({
        eventName: 'FALLBACK',
        data: {
          FALLBACK_LABEL: label,
          FALLBACK_TYPE: type,
          GENERIC_ERROR_MESSAGE: message
        }
      })
    },
    []
  )

  return {
    handleFallbackEvent,
    handleSuggestion,
    sendEventFallback
  }
}
